// GooglePlacesService.ts
import { CommonUtils } from '../../(common-lib)/CommonUtils';
import { DeploymentType, DeploymentUtils } from '../../(common-lib)/DeploymentUtils';
import type {
  AddressComponent,
  PlaceDetails,
  PlacePrediction,
  Suggestion,
  USState,
} from './types';
import {
  type StateLocationBias,
  type StateLocationRestriction,
  getStateLocationBias,
  getStateLocationRestriction,
} from './stateLocationBias';

declare global {
  interface Window {
    google: {
      maps: {
        places: {
          AutocompleteSuggestion: {
            fetchAutocompleteSuggestions: (request: {
              input: string;
              locationBias?: StateLocationBias;
              locationRestriction?: StateLocationRestriction;
              includedPrimaryTypes?: string[];
              includedRegionCodes?: string[];
              language?: string;
            }) => Promise<{
              suggestions: Array<{
                placePrediction: PlacePrediction;
              }>;
            }>;
          };
        };
      };
    };
  }
}

interface AutocompleteRequest {
  input: string;
  includedRegionCodes?: string[];
  language?: string;
  locationBias?: StateLocationBias;
  locationRestriction?: StateLocationRestriction;
  includedPrimaryTypes?: string[];
}

// Google Places runs in the browser, so the API key is visible client-side.
// For this integration, Key Vault or `.env.local` would not make the browser
// key secret, so we keep the deployment-specific keys directly in this file.
//
// Restriction: local and UAT intentionally share the same non-prod key. If
// they ever need to diverge, split `GOOGLE_MAPS_API_KEY_UAT_LOCAL` into
// separate constants and update `getGoogleMapsApiKey`.
const GOOGLE_MAPS_API_KEY_PROD      = 'AIzaSyDwbaUCj_oQTEIFL_zeC5OHhKMN8haMGhY';
const GOOGLE_MAPS_API_KEY_UAT_LOCAL = 'AIzaSyDAigULbSKlhcr3glyGOIaU0L-SLe4538g';

function getGoogleMapsApiKey(): string {
  if (typeof window === 'undefined') {
    return GOOGLE_MAPS_API_KEY_UAT_LOCAL;
  }

  const deploymentType = DeploymentUtils.getDeploymentType(window.location.hostname);
  return deploymentType === DeploymentType.PROD
    ? GOOGLE_MAPS_API_KEY_PROD
    : GOOGLE_MAPS_API_KEY_UAT_LOCAL;
}

export class GooglePlacesService {
  private static instance: GooglePlacesService;
  private loadingPromise: Promise<boolean> | null = null;

  private constructor() {}

  public static getInstance(): GooglePlacesService {
    if (!GooglePlacesService.instance) {
      GooglePlacesService.instance = new GooglePlacesService();
    }
    return GooglePlacesService.instance;
  }

  public async loadGoogleMapsAPI(): Promise<boolean> {
    if (typeof window === 'undefined') {
      return false;
    }

    if (this.isGoogleMapsLoaded()) {
      return true;
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise<boolean>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-google-maps-script="true"]');
      if (existingScript) {
        this.waitForAutocomplete(resolve, reject);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(getGoogleMapsApiKey())}&libraries=places&v=beta`;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-google-maps-script', 'true');

      script.onload = () => {
        if (this.isGoogleMapsLoaded()) {
          resolve(true);
          return;
        }
        reject(new Error('Google Maps Places API not available after loading'));
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    })
      .catch((error) => {
        this.loadingPromise = null;
        throw error;
      });

    try {
      return await this.loadingPromise;
    } catch (error) {
      CommonUtils.logError(
        '[Address][GooglePlacesService.loadGoogleMapsAPI] Failed to load Google Maps Places API.',
        error
      );
      return false;
    }
  }

  private waitForAutocomplete(
    resolve: (value: boolean) => void,
    reject: (reason?: unknown) => void,
    attempt: number = 0
  ) {
    if (this.isGoogleMapsLoaded()) {
      resolve(true);
      return;
    }

    if (attempt > 100) {
      reject(new Error('Timed out waiting for Google Maps Places API'));
      return;
    }

    setTimeout(() => this.waitForAutocomplete(resolve, reject, attempt + 1), 100);
  }

  public isGoogleMapsLoaded(): boolean {
    if (typeof window === 'undefined') return false;
    return !!window.google?.maps?.places?.AutocompleteSuggestion;
  }

  public async fetchSuggestions(input: string, stateHint?: USState): Promise<Suggestion[]> {
    if (!this.isGoogleMapsLoaded()) {
      throw new Error('Google Maps API not loaded. Call loadGoogleMapsAPI() first.');
    }

    const trimmedInput = (input || '').trim();
    if (trimmedInput.length < 3) {
      return [];
    }

    const request: AutocompleteRequest = {
      input: trimmedInput,
      includedRegionCodes: ['US'],
    };

    const stateCode = (stateHint?.code || '').trim().toUpperCase();
    const locationRestriction = getStateLocationRestriction(stateCode);
    if (locationRestriction) {
      request.locationRestriction = locationRestriction;
    } else {
      const locationBias = getStateLocationBias(stateCode);
      if (locationBias) {
        request.locationBias = locationBias;
      }
    }

    const response = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    return (response?.suggestions || []).map(({ placePrediction }) => {
      return this.mapSuggestion(placePrediction);
    });
  }

  private mapSuggestion(placePrediction: PlacePrediction): Suggestion {
    const fullText = placePrediction?.text?.text || '';
    const mainText = placePrediction?.structuredFormat?.mainText?.text || '';
    const secondaryText = placePrediction?.structuredFormat?.secondaryText?.text || '';
    const structuredText = [mainText, secondaryText].filter(Boolean).join(', ');
    const description = fullText && fullText.length >= structuredText.length
      ? fullText
      : (structuredText || fullText);

    return {
      placeId: placePrediction?.placeId || '',
      description,
      mainText: mainText || description,
      secondaryText: secondaryText || '',
      placePrediction,
    };
  }

  public async getPlaceDetails(
    suggestion: Suggestion,
    fields: string[] = ['addressComponents', 'formattedAddress', 'location', 'displayName']
  ): Promise<PlaceDetails> {
    if (!this.isGoogleMapsLoaded()) {
      throw new Error('Google Maps API not loaded. Call loadGoogleMapsAPI() first.');
    }

    const { place } = await suggestion.placePrediction.toPlace().fetchFields({ fields });
    const addressDetails = this.extractAddressComponents(place?.addressComponents || []);

    return {
      ...addressDetails,
      formattedAddress: place?.formattedAddress || '',
      addressComponents: place?.addressComponents || [],
    };
  }

  private extractAddressComponents(
    addressComponents: AddressComponent[]
  ): Omit<PlaceDetails, 'formattedAddress' | 'addressComponents'> {
    let streetNumber = '';
    let route = '';
    let subpremise = '';
    let city = '';
    let zipCode = '';
    let stateCode = '';
    let stateName = '';

    addressComponents.forEach((component) => {
      const types = component.types || [];

      if (types.includes('street_number')) {
        streetNumber = component.longText;
      } else if (types.includes('route')) {
        route = component.longText;
      } else if (types.includes('subpremise')) {
        subpremise = component.longText;
      } else if (types.includes('locality') || types.includes('sublocality')) {
        city = component.longText;
      } else if (
        !city
        && (
          types.includes('postal_town')
          || types.includes('sublocality_level_1')
          || types.includes('neighborhood')
          || types.includes('administrative_area_level_3')
          || types.includes('administrative_area_level_2')
        )
      ) {
        city = component.longText;
      } else if (types.includes('postal_code')) {
        zipCode = component.longText || component.shortText || zipCode;
      } else if (!zipCode && types.includes('postal_code_prefix')) {
        zipCode = component.longText || component.shortText || zipCode;
      } else if (types.includes('postal_code_suffix')) {
        const suffix = component.longText || component.shortText || '';
        if (suffix) {
          zipCode = zipCode ? `${zipCode}-${suffix}` : suffix;
        }
      } else if (types.includes('administrative_area_level_1')) {
        stateName = component.longText;
        stateCode = component.shortText;
      }
    });

    return {
      streetNumber,
      route,
      subpremise,
      city,
      zipCode,
      stateCode,
      stateName,
      addressLine1: `${streetNumber} ${route}`.trim(),
      addressLine2: subpremise,
    };
  }
}
